import type { ReactNode } from 'react';
import { initialsOf } from '../lib/format';
import styles from './avatar.module.css';

export type AvatarKind = 'agent' | 'person' | 'system';
export type AvatarSize = 'sm' | 'md' | 'lg';

export interface AvatarProps {
  name: string;
  /** Agents carry an explicit `initial` column; people are derived from the name. */
  initial?: string | null;
  kind?: AvatarKind;
  size?: AvatarSize;
  className?: string;
}

export function Avatar({ name, initial, kind = 'person', size = 'md', className }: AvatarProps) {
  const glyph = (initial && initial.trim().slice(0, 2)) || initialsOf(name);
  return (
    <span
      className={[styles.avatar, styles[size], styles[kind], className].filter(Boolean).join(' ')}
      title={name}
      aria-hidden="true"
    >
      {glyph}
    </span>
  );
}

export interface AgentChipProps {
  name: string;
  roleLabel?: string | null;
  initial?: string | null;
  size?: AvatarSize;
  trailing?: ReactNode;
  className?: string;
}

/** Read-only identity chip for an agent. The switcher builds its own button. */
export function AgentChip({
  name,
  roleLabel,
  initial,
  size = 'md',
  trailing,
  className,
}: AgentChipProps) {
  return (
    <span className={[styles.chip, className].filter(Boolean).join(' ')}>
      <Avatar name={name} initial={initial} kind="agent" size={size} />
      <span className={styles.chipText}>
        <span className={styles.chipName}>{name}</span>
        {roleLabel ? <span className={styles.chipRole}>{roleLabel}</span> : null}
      </span>
      {trailing}
    </span>
  );
}

export { styles as agentChipStyles };
